import { useState } from "react";
import type { GuidanceDocument } from "@keydials/api-client";
import { Icon } from "./Icon";

/**
 * Author-provided documents attached to a node's guidance (KEY-364) — the template, checklist or form the
 * dial ships with. Distinct from the Files tab, which holds what users upload against a running dial: these
 * come with the definition, are the same for everyone, and are read-only here.
 *
 * A document whose `downloadUrl` is missing is skipped rather than rendered dead. That happens when the
 * server can't back the reference — an import from elsewhere — and a row you can see but can't open is
 * worse than one that isn't listed, because it looks like a broken app rather than a missing file.
 *
 * A PDF or an image can also be previewed in place (KEY-565), from the same short-lived URL. Collapsed until
 * asked for, and one at a time: a guidance tab can list twenty documents, and opening it on a phone must not
 * start twenty downloads. Everything else stays a plain link — a browser can't show a spreadsheet inline.
 */
export function GuidanceDocuments({ documents }: { documents?: GuidanceDocument[] | null }) {
  const list = (documents ?? []).filter((d) => !!d.downloadUrl);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  if (list.length === 0) {
    return null;
  }
  return (
    <div style={{ marginTop: 12, borderTop: "var(--border-w) dashed var(--border)", paddingTop: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)", marginBottom: 10 }}>
        Documents · {list.length}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {list.map((doc) => {
          const preview = previewKind(doc);
          const open = preview !== null && previewKey === doc.key;
          const name = doc.title || doc.fileName;
          return (
            <div key={doc.key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
                <a
                  href={doc.downloadUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  // The blob is stored under an opaque id, so without this a download lands as a meaningless
                  // GUID; the author's file name is what people expect to find in their downloads folder.
                  download={doc.fileName}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 11px",
                    borderRadius: "var(--radius-sm)",
                    border: "var(--border-w) solid var(--border)",
                    background: "var(--surface-2)",
                    textDecoration: "none",
                    color: "var(--text)",
                  }}
                >
                  <Icon name="file" size={15} style={{ color: "var(--accent)", flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {doc.title || doc.fileName}
                    </span>
                    <span style={{ display: "block", fontSize: 11.5, color: "var(--text-faint)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {describe(doc)}
                    </span>
                  </span>
                  <Icon name="download" size={15} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
                </a>
                {preview && (
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-label={open ? `Hide preview of ${name}` : `Preview ${name}`}
                    onClick={() => setPreviewKey(open ? null : doc.key)}
                    style={{
                      flexShrink: 0,
                      padding: "0 12px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      borderRadius: "var(--radius-sm)",
                      border: `var(--border-w) solid ${open ? "var(--accent-border)" : "var(--border)"}`,
                      background: open ? "var(--accent-soft)" : "var(--surface-2)",
                      color: open ? "var(--accent-text)" : "var(--text-muted)",
                    }}
                  >
                    {open ? "Hide" : "Preview"}
                  </button>
                )}
              </div>
              {open && preview && <DocumentPreview doc={doc} kind={preview} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Which inline preview a document gets, if any (KEY-565). A PDF renders in the browser's own viewer and an image
 * in an `<img>`, both straight from the SAS URL — the upload stores the file's real content type and the read URL
 * forces no download, so the browser shows it rather than saving it. Anything else has no in-page rendering, so
 * it stays a link.
 */
export function previewKind(doc: GuidanceDocument): "pdf" | "image" | null {
  const type = doc.contentType.toLowerCase();
  if (type === "application/pdf") {
    return "pdf";
  }
  // An <img> never runs script, so any image type is safe to show this way — the upload allow-list only
  // lets PNG and JPEG in regardless.
  return type.startsWith("image/") ? "image" : null;
}

/** The expanded preview. Bounded by the viewport as well as a fixed cap, so on a phone it can never take the
 *  whole screen and strand the reader inside a PDF with nothing left to scroll the page by. */
function DocumentPreview({ doc, kind }: { doc: GuidanceDocument; kind: "pdf" | "image" }) {
  const frame = {
    display: "block",
    borderRadius: "var(--radius-sm)",
    border: "var(--border-w) solid var(--border)",
    background: "var(--surface-2)",
  } as const;
  if (kind === "image") {
    return (
      <img
        src={doc.downloadUrl!}
        alt={doc.title || doc.fileName}
        loading="lazy"
        style={{ ...frame, maxWidth: "100%", maxHeight: "min(60vh, 480px)", objectFit: "contain" }}
      />
    );
  }
  // No `sandbox`: Chrome refuses to run its PDF viewer in a sandboxed frame and shows a blocked-plugin page
  // instead. The frame is cross-origin (blob storage), so it can't reach this page either way.
  return (
    <iframe
      src={doc.downloadUrl!}
      title={`Preview of ${doc.title || doc.fileName}`}
      loading="lazy"
      style={{ ...frame, width: "100%", height: "min(70vh, 560px)" }}
    />
  );
}

/** "PDF · 1.2 MB" — the two things that decide whether someone opens it on the phone they're holding. */
function describe(doc: GuidanceDocument): string {
  const type = fileType(doc.fileName, doc.contentType);
  const size = fileSize(doc.sizeBytes);
  return [type, size].filter(Boolean).join(" · ");
}

function fileType(fileName: string, contentType: string): string {
  const ext = (fileName.split(".").pop() ?? "").toUpperCase();
  // The extension is what the author named it; the content type is the fallback for a name without one.
  return ext && ext.length <= 5 ? ext : (contentType.split("/").pop() ?? "file").toUpperCase();
}

export function fileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kb = bytes / 1024;
  return kb < 1024 ? `${Math.round(kb)} KB` : `${(kb / 1024).toFixed(1)} MB`;
}
